import { ColumnDef } from "@tanstack/react-table";
import { useMemo, useState } from "react";

import { IAdminUser } from "../../../@types/admin/AdminAccess";
import { TableSorted } from "../../../components/TableSorted";
import {
  useAssignUserRole,
  useGrantUserPermission,
  useRevokeUserPermission,
} from "../../../hooks/mutations/admin/access";
import {
  useAdminUsers,
  useUserAccessPermissionCatalog,
  useUserAccessRoles,
  useUserDirectPermissions,
} from "../../../hooks/queries/admin/access";
import { useModal } from "../../../hooks/useModal";
import { useToast } from "../../../hooks/useToast";
import { getLongFormattedDate } from "../../../utils/dateFormatter";
import { errorToastMessage } from "../../../utils/HttpError";
import {
  AdminSelectField,
  ErrorMessage,
  FilterButton,
  FilterGroup,
  FilterLabel,
  FiltersContainer,
  ModalContainer,
  ModalForm,
  ModalFormActions,
  ModalTitle,
  PageContainer,
  PageHeader,
  PageTitle,
  SearchInput,
  TableContainer,
  TableLoadingState,
  TablePagination,
  TableWrapper,
} from "../shared";
import { RoleCodeCell, SectionLabel, TypeBadge } from "./Access.styles";
import { PermissionChecklist } from "./PermissionChecklist";

interface IManageUserAccessModalProps {
  user: IAdminUser;
  onClose: () => void;
}

/**
 * Manage one user's role (single role, saved explicitly) and direct
 * permission grants (toggles apply immediately). EN-5129.
 */
function ManageUserAccessModal({ user, onClose }: IManageUserAccessModalProps) {
  const { showToast } = useToast();
  const { roles } = useUserAccessRoles();
  const { permissionCatalog } = useUserAccessPermissionCatalog();
  const { directPermissions, isLoading: permissionsLoading } =
    useUserDirectPermissions(user.cmsUserId);
  const { assignUserRole, isAssigningUserRole } = useAssignUserRole();
  const { grantUserPermission } = useGrantUserPermission();
  const { revokeUserPermission } = useRevokeUserPermission();

  const [roleCode, setRoleCode] = useState(user.roleCode ?? "");
  const [isTogglingPermission, setIsTogglingPermission] = useState(false);

  // The role's own permissions show as checked-and-locked in the checklist,
  // so the modal reads as the user's effective access. Follows the (unsaved)
  // select so picking a role previews what it brings.
  const inheritedPermissions = useMemo(
    () =>
      new Set(roles.find((role) => role.code === roleCode)?.permissionCodes),
    [roles, roleCode]
  );

  const handleSaveRole = async () => {
    try {
      await assignUserRole({ cmsUserId: user.cmsUserId, roleCode });
      showToast("Role updated", "success");
      onClose();
    } catch (error) {
      showToast(
        errorToastMessage(error, "Failed to update the role"),
        "warning"
      );
    }
  };

  const handleTogglePermission = async (
    permissionCode: string,
    grant: boolean
  ) => {
    setIsTogglingPermission(true);
    try {
      if (grant) {
        await grantUserPermission({
          cmsUserId: user.cmsUserId,
          permissionCode,
        });
      } else {
        await revokeUserPermission({
          cmsUserId: user.cmsUserId,
          permissionCode,
        });
      }
    } catch (error) {
      showToast(
        errorToastMessage(
          error,
          `Failed to ${grant ? "grant" : "revoke"} ${permissionCode}`
        ),
        "warning"
      );
    } finally {
      setIsTogglingPermission(false);
    }
  };

  return (
    <ModalContainer>
      <ModalTitle>
        {user.email || `User ${user.cmsUserId}`} (#{user.cmsUserId})
      </ModalTitle>
      <ModalForm>
        <FilterGroup>
          <FilterLabel>Role</FilterLabel>
          <AdminSelectField
            value={roleCode}
            onChange={(e) => setRoleCode(e.target.value)}
          >
            <option value="">No role</option>
            {roles.map((role) => (
              <option key={role.code} value={role.code}>
                {role.displayName}
              </option>
            ))}
          </AdminSelectField>
        </FilterGroup>

        <SectionLabel>
          Permissions — the role&apos;s own are locked; the rest are direct
          grants on top
        </SectionLabel>
        {permissionsLoading ? (
          <ErrorMessage>Loading permissions...</ErrorMessage>
        ) : (
          <PermissionChecklist
            catalog={permissionCatalog}
            granted={new Set(directPermissions)}
            inherited={inheritedPermissions}
            disabled={isTogglingPermission}
            onToggle={handleTogglePermission}
          />
        )}

        <ModalFormActions>
          <FilterButton variant="secondary" onClick={onClose}>
            Close
          </FilterButton>
          <FilterButton
            variant="primary"
            disabled={
              !roleCode || roleCode === user.roleCode || isAssigningUserRole
            }
            onClick={handleSaveRole}
          >
            {isAssigningUserRole ? "Saving..." : "Save role"}
          </FilterButton>
        </ModalFormActions>
      </ModalForm>
    </ModalContainer>
  );
}

const DEFAULT_PAGE_SIZE = 10;

/**
 * User Access admin page (EN-5129, portal-only — never existed in Retool):
 * every user (staff/patient) with id, email, dates and type, plus per-user
 * role assignment and direct permission grants.
 */
export function AdminUserAccess() {
  document.title = "User access - Admin";

  const { openModal, closeModal } = useModal();
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const { users, pagination, isLoading, isError } = useAdminUsers(
    search,
    page,
    pageSize
  );
  // Same cached catalog the manage modal uses — here it turns the row's
  // role CODE into the name the role select shows ("super-admin" → "Super
  // Admin"), falling back to the code until it loads.
  const { roles } = useUserAccessRoles();

  const handleSearchChange = (value: string) => {
    setSearch(value);
    setPage(1);
  };

  const handleManageClick = (user: IAdminUser) => {
    openModal(<ManageUserAccessModal user={user} onClose={closeModal} />, {
      // Wide enough for the checklist's two columns; it drops to one below
      // 720px (see ChecklistGrid).
      width: "min(820px, 94vw)",
      maxHeight: "90vh",
      showCloseButton: true,
    });
  };

  // Sortable like every other /admin table (EN-5146 UI feedback) — this was
  // the only one whose headers were all inert. The list is paginated
  // server-side and the endpoint takes no sort parameter, so this orders the
  // page that has been fetched, which is what the other server-paginated
  // pages here already do.
  const columns: ColumnDef<IAdminUser, string>[] = useMemo(
    () => [
      { accessorKey: "cmsUserId", header: "ID", enableSorting: true },
      {
        accessorKey: "email",
        header: "Email",
        cell: ({ row }) => <span>{row.original.email || "-"}</span>,
        enableSorting: true,
      },
      {
        accessorKey: "userType",
        header: "Type",
        cell: ({ row }) => (
          <TypeBadge userType={row.original.userType}>
            {row.original.userType}
          </TypeBadge>
        ),
        enableSorting: true,
      },
      {
        accessorKey: "roleCode",
        header: "Role",
        cell: ({ row }) => {
          const { roleCode } = row.original;
          if (!roleCode) return <span>-</span>;
          const displayName = roles.find(
            (role) => role.code === roleCode
          )?.displayName;
          return displayName ? (
            <span>{displayName}</span>
          ) : (
            <RoleCodeCell>{roleCode}</RoleCodeCell>
          );
        },
        enableSorting: true,
      },
      {
        accessorKey: "dateJoined",
        header: "Date joined",
        cell: ({ row }) => (
          <span>
            {row.original.dateJoined
              ? getLongFormattedDate(row.original.dateJoined)
              : "-"}
          </span>
        ),
        enableSorting: true,
      },
      {
        accessorKey: "lastLogin",
        header: "Last login",
        cell: ({ row }) => (
          <span>
            {row.original.lastLogin
              ? getLongFormattedDate(row.original.lastLogin)
              : "-"}
          </span>
        ),
        enableSorting: true,
      },
    ],
    [users, roles]
  );

  return (
    <PageContainer>
      <PageHeader>
        <PageTitle>User access</PageTitle>
      </PageHeader>

      <FiltersContainer>
        <SearchInput
          type="text"
          placeholder="Search by email or user ID..."
          value={search}
          onChange={(e) => handleSearchChange(e.target.value)}
        />
      </FiltersContainer>

      {isError && (
        <ErrorMessage>
          Failed to load users. Try refreshing the page.
        </ErrorMessage>
      )}

      <TableContainer>
        <TableWrapper refreshKeys={[["admin-user-access-users"]]}>
          <TableSorted
            columns={columns}
            data={users}
            onRowClick={handleManageClick}
            emptyStateText={
              isLoading ? <TableLoadingState /> : "No users found"
            }
          />
        </TableWrapper>
        <TablePagination
          // Server-paginated: the export can only write the page that has been
          // fetched, which the button's own row count spells out.
          exportCsv={{
            filename: "user-access",
            columns,
            rows: users,
            currentPageOnly: true,
          }}
          pagination={pagination}
          onPageChange={setPage}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setPage(1);
          }}
        />
      </TableContainer>
    </PageContainer>
  );
}
